/* eslint-disable babel/camelcase */
import React from 'react';
import { Alert, Nav } from 'react-bootstrap';
import { UserRoles } from '~/components/Admin';
import { getPasswordRequirements } from '~/components/Auth/passwordRequirements';
import { Form, FormGroup, ValidationMessage } from '~/components/Form';
import { ClientContext, ServicesContext, useAdminService } from '~/services';
import { OptionWithDescriptionType, Select } from '~/components/Select';
import { Card } from '~/components/Card';
import { enums } from '~/util';
import { NewUserRolesType } from '~/services/AdminService/useAddUserRoles';

export interface AddedRoleType {
    id: number;
    name: string;
    tin?: string;
    role: OptionWithDescriptionType;
    markets: { label: string; value: { id: number; name: string } }[];
}

export type EntityTypeType = 'organization' | 'market' | 'macl';

type AccessTypeOptionsType = 'tin_users' | 'macl_users' | 'market_users';

export interface NewUserType {
    first_name: string;
    last_name: string;
    email: string;
    title: string;
    global_roles: string[];
    access_type: number;
    _send_welcome_email?: boolean;
    password?: string;
    tin_users?: NewUserRolesType[];
    macl_users?: NewUserRolesType[];
    market_users?: NewUserRolesType[];
}

interface UserFormProps {
    formId: string;
    setSubmitting: (x: boolean) => void;
    submitting: boolean;
    setShow: (x: boolean) => void;
}
export function UserForm({ formId, setSubmitting, submitting, setShow }: UserFormProps) {
    const context = React.useContext(ServicesContext);
    const client = React.useContext(ClientContext);
    const { createUser } = useAdminService();
    const [firstName, setFirstName] = React.useState('');
    const [lastName, setLastName] = React.useState('');
    const [email, setEmail] = React.useState('');
    const [title, setTitle] = React.useState('');
    const [accessType, setAccessType] = React.useState({ value: '1', label: 'TIN' });
    const [pw, setPw] = React.useState('');
    const [confirmpw, setConfirmPw] = React.useState('');
    const [addedRoles, setAddedRoles] = React.useState<AddedRoleType[]>([]);
    const [sendWelcome, setSendWelcome] = React.useState(true);
    const [hasError, setHasError] = React.useState(false);
    const [serverError, setServerError] = React.useState<null | string | CustomErrorArray>(null);
    const [tab, setTab] = React.useState('info');

    let accessEntityName = `${enums(['user', 'access_type', parseInt(accessType.value)])}s`;
    if (accessEntityName === 'TINs') accessEntityName = 'Organizations';
    const accessEntityType = accessEntityName.slice(0, -1).toLowerCase();

    async function handleSubmit() {
        const user: NewUserType = {
            first_name: firstName,
            last_name: lastName,
            email: email.toLowerCase(),
            title,
            global_roles: [],
            access_type: parseInt(accessType.value),
        };

        if (sendWelcome) {
            // eslint-disable-next-line no-underscore-dangle
            user._send_welcome_email = true;
        } else {
            user.password = pw;
        }

        if (addedRoles.length > 0) {
            user[`${accessEntityType}_users` as AccessTypeOptionsType] = addedRoles.map((x) => {
                const data: NewUserRolesType = {
                    [accessEntityType]: { id: x.id },
                    [`${accessEntityType}_roles`]: [x.role.value],
                };
                if (x?.markets && x?.markets?.length > 0) {
                    data.markets = x.markets.map((y) => ({ id: y.value.id }));
                }
                return data;
            });
        }

        try {
            setSubmitting(true);
            await createUser(user, { handledErrors: { 400: false } });
            setShow(false);
        } catch (e) {
            setHasError(true);
            setServerError(e.response);
        } finally {
            setSubmitting(false);
        }
    }

    function handleAccessTypeChange(value: { value: string; label: string }) {
        setAccessType(value);
        setAddedRoles([]);
    }

    React.useEffect(() => {
        if (!client.isMarketValidEmailDomain(email) && accessType.label === 'Market') {
            handleAccessTypeChange({ value: '1', label: 'TIN' });
        }
    }, [email, accessType.label, client]);

    return (
        <Form
            id={formId}
            disabled={submitting}
            onSubmit={handleSubmit}
            values={{ firstName, lastName, title, email, pw, confirmpw }}
        >
            <div className="card-header user-info-tabs">
                <Nav variant="tabs" className="card-header-tabs">
                    <Nav.Item>
                        <Nav.Link
                            className={tab === 'info' ? 'active' : ''}
                            onClick={() => setTab('info')}
                        >
                            Information
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link
                            className={tab === 'access' ? 'active' : ''}
                            onClick={() => setTab('access')}
                        >
                            Access
                        </Nav.Link>
                    </Nav.Item>
                </Nav>
            </div>
            {tab === 'info' && (
                <Card
                    className="mb-2"
                    header={<div className="card-header">Basic Information</div>}
                >
                    <div className="row">
                        <FormGroup htmlFor="firstName" className="col-md-6">
                            <label htmlFor="firstName" className="col-form-label">
                                First Name
                            </label>
                            <input
                                type="text"
                                id="firstName"
                                className="form-control"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                required
                            />
                            <ValidationMessage type="required">
                                Please enter a First Name.
                            </ValidationMessage>
                        </FormGroup>
                        <FormGroup htmlFor="lastName" className="col-md-6">
                            <label htmlFor="lastName" className="col-form-label">
                                Last Name
                            </label>
                            <input
                                type="text"
                                id="lastName"
                                className="form-control"
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                required
                            />
                            <ValidationMessage type="required">
                                Please enter a Last Name.
                            </ValidationMessage>
                        </FormGroup>
                    </div>
                    <div className="row">
                        <FormGroup htmlFor="title" className="col-md-6">
                            <label htmlFor="title" className="col-form-label">
                                Title
                            </label>
                            <input
                                type="text"
                                id="title"
                                className="form-control"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                            />
                        </FormGroup>
                    </div>
                    <FormGroup htmlFor="email">
                        <label htmlFor="email" className="col-form-label">
                            Email
                        </label>
                        <input
                            type="email"
                            id="email"
                            className="form-control"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                        <ValidationMessage type="email">
                            Please enter a valid email address.
                        </ValidationMessage>
                    </FormGroup>
                    <FormGroup htmlFor="accessType">
                        <label htmlFor="accessType" className="col-form-label">
                            Access Type
                        </label>
                        <Select
                            id="accessType"
                            options={client.availableUserAccessTypes(email)}
                            value={accessType}
                            placeholder="Access Type"
                            label="Access Type"
                            menuPosition="fixed"
                            onChange={handleAccessTypeChange}
                            isDisabled={!context.user.isGlobalAdmin()}
                        />
                    </FormGroup>
                    <FormGroup htmlFor="sendWelcome">
                        <input
                            type="checkbox"
                            id="sendWelcome"
                            className="me-1"
                            onChange={(e) => setSendWelcome(e.target.checked)}
                            checked={sendWelcome}
                        />
                        <label htmlFor="sendWelcome" className="col-form-label">
                            Send Welcome Email
                        </label>
                    </FormGroup>
                    {!sendWelcome && (
                        <>
                            <FormGroup htmlFor="pw">
                                <label htmlFor="pw" className="col-form-label">
                                    New Password
                                </label>
                                <input
                                    type="password"
                                    id="pw"
                                    className="form-control"
                                    value={pw}
                                    onChange={(e) => setPw(e.target.value)}
                                    required
                                />
                                <ValidationMessage type="required">
                                    Password is required.
                                </ValidationMessage>
                            </FormGroup>
                            <FormGroup htmlFor="confirmpw">
                                <label htmlFor="confirmpw" className="col-form-label">
                                    Confirm Password
                                </label>
                                <input
                                    type="password"
                                    id="confirmpw"
                                    className="form-control"
                                    data-match="pw"
                                    value={confirmpw}
                                    onChange={(e) => setConfirmPw(e.target.value)}
                                    required
                                />
                                <ValidationMessage type="required">
                                    Password confirmation is required.
                                </ValidationMessage>
                                <ValidationMessage type="match">
                                    Passwords must match.
                                </ValidationMessage>
                            </FormGroup>
                            {getPasswordRequirements()}
                        </>
                    )}
                </Card>
            )}
            <UserRoles
                show={tab === 'access'}
                entityType={accessEntityType as EntityTypeType}
                entityLabel={accessEntityName}
                addedRoles={addedRoles}
                setAddedRoles={setAddedRoles}
            />
            {hasError && (
                <Alert variant="danger" className="mt-sm-3">
                    {Array.isArray(serverError) ? (
                        <ul className="mb-0">
                            {serverError.map((x) => (
                                <li key={x.message}>{x.message}</li>
                            ))}
                        </ul>
                    ) : (
                        (serverError as string)
                    )}
                </Alert>
            )}
        </Form>
    );
}
